import { useStytch } from "@stytch/react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { FormField, FormItem, FormLabel } from "./ui/form";
import { toast } from "@/hooks/use-toast";
import {
  ArrowRight,
  ArrowLeft,
  EnvelopeSimple,
  Password,
} from "@phosphor-icons/react";
import { Link, useSearchParams } from "react-router-dom";
import {
  AUTHENTICATE_RESET_PASSWORD__DYNAMIC,
  AUTHENTICATE_SIGNUP__DYNAMIC,
} from "@/lib/links";
import OAuth from "./auth/OAuth";
import { useState } from "react";
import { StytchAPIError } from "@stytch/vanilla-js";
import { useInvalidateUserStats } from "@/lib/queries/user/useUserStats";
import { useTranslation } from "react-i18next";

export default function HeadlessLogin() {
  const { t } = useTranslation("auth");
  const stytchClient = useStytch();
  const queryClient = useQueryClient();
  const invalidateUserStats = useInvalidateUserStats();
  const [currentStep, setCurrentStep] = useState(1);
  const [userEmail, setUserEmail] = useState("");
  const [searchParams] = useSearchParams();
  const returnTo = searchParams.get("returnTo") || "/";

  const form = useForm({
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Handle magic link authentication
  const sendEmailMagicLinkMutation = useMutation({
    mutationKey: ["sendEmailMagicLink"],
    mutationFn: (email: string) => {
      return stytchClient.magicLinks.email
        .send(email, {
          login_magic_link_url: `${import.meta.env.VITE_DOMAIN}/authenticate${returnTo !== "/" && returnTo !== "" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`,
        })
        .then((response) => {
          if (response.status_code === 200) {
            return response;
          } else {
            throw new Error(t("login.errors.failedToSendMagicLink"));
          }
        });
    },
    onSuccess: (_response) => {},
    onError: (_error) => {
      toast({
        variant: "destructive",
        title: "Error",
        description: t("login.errors.failedToSendMagicLink"),
      });
    },
  });

  // Handle password authentication
  const passwordLoginMutation = useMutation({
    mutationKey: ["passwordLogin"],
    mutationFn: ({ email, password }: { email: string; password: string }) => {
      return stytchClient.passwords
        .authenticate({
          email,
          password,
          session_duration_minutes: 60 * 24,
        })
        .then((response) => {
          if (response.status_code === 200) {
            queryClient.invalidateQueries({ queryKey: "init" });

            queryClient.refetchQueries({ queryKey: "init" });
            queryClient.invalidateQueries({ queryKey: "user" });

            queryClient.refetchQueries({ queryKey: "user" });
            invalidateUserStats();
            return response;
          } else {
            throw new Error(t("login.errors.loginFailed"));
          }
        })
        .catch((error: StytchAPIError) => {
          switch (error.error_type) {
            case "invalid_email":
              throw new Error(t("login.errors.invalidCredentials"));
            case "invalid_email_sandbox":
              throw new Error(t("login.errors.invalidCredentials"));
            case "no_user_password":
              throw new Error("Email");
            case "unauthorized_credentials":
              throw new Error("login.errors.invalidCredentials");
            case "reset_password":
              throw new Error(
                "You must reset your password before logging in."
              );
            case "session_not_mutable_impersonated":
              throw new Error("Session not mutable. Please try again.");
            default:
              throw new Error(t("login.errors.unknownError"));
          }
        });
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "You've been successfully logged in.",
      });
      window.location.href = decodeURIComponent(returnTo);
    },
    onError: (e) => {
      toast({
        variant: "destructive",
        title: t("login.errors.loginFailed"),
        description: t(e.message),
      });
    },
  });

  // Handle email submission (Step 1)
  const handleEmailSubmit = (data: { email: string }) => {
    if (data.email) {
      setUserEmail(data.email);
      //handleMagicLinkOption(data.email);
      setCurrentStep(2);
    }
  };

  const handleEmailResend = () => {
    handleMagicLinkOption();
    setCurrentStep(3);
  };

  // Handle magic link option
  const handleMagicLinkOption = (email: string | null = null) => {
    sendEmailMagicLinkMutation.mutate(email ? email : userEmail);
  };

  // Handle password login option
  const handlePasswordLogin = (data: { password: string }) => {
    if (data.password) {
      passwordLoginMutation.mutate({
        email: userEmail,
        password: data.password,
      });
    }
  };

  // Reset the form and go back to step 1
  const handleBack = () => {
    setCurrentStep(1);
    passwordLoginMutation.reset();
  };

  // Magic link success screen
  if (sendEmailMagicLinkMutation.isSuccess && currentStep === 3) {
    return (
      <div className="flex flex-col items-start justify-center pl-12 mt-2">
        <h1 className="mb-4 text-2xl font-bold">
          {t("login.checkYourEmail.title")}
        </h1>
        <p className="mb-4 text-lg text-gray-700">
          {t("login.checkYourEmail.description")} <strong>{userEmail}</strong>.
        </p>
        <p className="text-sm text-gray-500">
          {t("login.checkYourEmail.instructions")}
        </p>
        <Button
          className="mt-4"
          onClick={() => {
            sendEmailMagicLinkMutation.reset();
            setCurrentStep(1);
            form.reset();
          }}
        >
          {t("login.resendMagicLink")}
        </Button>
        <Link
          to={`/authenticate/reset-password${returnTo !== "/" ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`}
          className="mt-2 text-sm underline text-light-grey decoration-dashed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
        >
          {t("login.forgotPassword")}
        </Link>
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      {currentStep === 1 ? (
        <form
          onSubmit={form.handleSubmit(handleEmailSubmit)}
          className="flex flex-col w-full max-w-md gap-4 px-4 sm:px-0 mt-7"
        >
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("login.email.label")}</FormLabel>
                <Input
                  {...field}
                  type="email"
                  placeholder={t("login.email.placeholder")}
                  className="!border-none"
                  required
                />
              </FormItem>
            )}
          />
          <div>
            <Button type="submit" size={"lg"} className="w-full">
              {t("login.continue")}
              <ArrowRight className="ml-2" />
            </Button>
            <div className="flex flex-col items-start justify-between mt-2 md:items-center md:flex-row">
              <Link
                to={AUTHENTICATE_SIGNUP__DYNAMIC(returnTo)}
                className="text-sm underline text-light-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {t("login.dontHaveAccount")}
              </Link>
              <Link
                to={AUTHENTICATE_RESET_PASSWORD__DYNAMIC(returnTo)}
                className="text-sm underline text-light-grey focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                {t("login.forgotPassword")}
              </Link>
            </div>
          </div>
          <p className="font-semibold text-center text-light-grey">
            {t("login.orLoginUsing")}
          </p>
          <OAuth returnTo={returnTo} />
        </form>
      ) : (
        <div className="flex flex-col w-full max-w-md gap-4 mt-7">
          <div className="flex items-center mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="p-0 px-2 mr-2"
            >
              <ArrowLeft className="mr-1" /> {t("login.back")}
            </Button>
            <p className="text-sm text-gray-500">
              {t("login.loggingInAs")} <strong>{userEmail}</strong>
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Magic Link Option */}
            <Button
              variant="outline"
              size="lg"
              className="flex items-center justify-center gap-2 p-6"
              onClick={handleEmailResend}
              disabled={sendEmailMagicLinkMutation.isPending}
            >
              <EnvelopeSimple size={24} />
              <div className="flex flex-col items-start">
                <span className="text-lg font-medium">
                  {t("login.sendMagicLink")}
                </span>
              </div>
            </Button>

            {/* Password Option */}
            {passwordLoginMutation.isPending ? (
              <Button
                size="lg"
                className="flex items-center justify-center gap-2 p-6"
                disabled
              >
                {t("login.loggingIn")}
              </Button>
            ) : (
              <form
                onSubmit={form.handleSubmit((data) =>
                  handlePasswordLogin({ password: data.password })
                )}
              >
                <div className="p-4 border rounded-lg">
                  <FormField
                    name="password"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t("login.password.label")}</FormLabel>
                        <div className="flex items-center gap-2">
                          <div className="flex flex-col items-center w-full">
                            <Input
                              {...field}
                              type="password"
                              placeholder={t("login.password.placeholder")}
                              className="flex-grow"
                              required
                            />
                            {/* errors */}
                          </div>
                          <Button type="submit" size="sm">
                            <Password size={20} className="mr-2" />
                            {t("login.login")}
                          </Button>
                        </div>
                        {passwordLoginMutation.isError && (
                          <span className="self-start mt-1 text-sm text-destructive">
                            {t(passwordLoginMutation.error.message)}
                          </span>
                        )}
                      </FormItem>
                    )}
                  />
                </div>
              </form>
            )}
          </div>
          <div className="flex flex-row items-center justify-between">
            <Link
              to={AUTHENTICATE_RESET_PASSWORD__DYNAMIC(returnTo)}
              className="mt-2 text-sm text-center underline text-light-grey"
            >
              {t("login.forgotPassword")}
            </Link>
            <Link
              to={AUTHENTICATE_SIGNUP__DYNAMIC(returnTo)}
              className="mt-2 text-sm text-center underline text-light-grey"
            >
              {t("login.dontHaveAccount")}
            </Link>
          </div>

          <p className="mt-4 font-semibold text-center text-light-grey">
            {t("login.orLoginUsing")}
          </p>
          <OAuth returnTo={returnTo} />
        </div>
      )}
    </FormProvider>
  );
}
